/**
 * Offline Manager
 * Handles online/offline events and processes queued requests
 */

import axiosWrapper from './axiosWrapper';
import { isOnline, getQueueSize } from './offlineQueue';

let isInitialized = false;
let processingQueue = false;

/**
 * Process the queue when back online
 */
const processQueue = async () => {
  // Prevent concurrent processing
  if (processingQueue) {
    console.log('[offlineManager] Queue processing already in progress');
    return;
  }

  if (!isOnline()) {
    console.log('[offlineManager] Still offline, skipping queue processing');
    return;
  }

  const queueSize = getQueueSize();
  if (queueSize === 0) {
    return;
  }

  processingQueue = true;
  console.log(`[offlineManager] Processing ${queueSize} queued requests...`);

  try {
    const result = await axiosWrapper.processQueue();
    console.log(`[offlineManager] Queue processing complete:`, result);

    // Show notification to user if there were failures
    if (result.failed > 0) {
      console.warn(`[offlineManager] ${result.failed} requests failed to process`);
    }
  } catch (error) {
    console.error('[offlineManager] Error processing queue:', error);
  } finally {
    processingQueue = false;
  }
};

/**
 * Handle online event
 */
const handleOnline = () => {
  console.log('[offlineManager] Connection restored, processing queued requests...');
  // Small delay to ensure connection is stable
  setTimeout(() => {
    processQueue();
  }, 1000);
};

/**
 * Handle offline event
 */
const handleOffline = () => {
  console.log('[offlineManager] Connection lost, requests will be queued');
};

/**
 * Initialize offline manager
 * Sets up event listeners for online/offline events
 */
export const initOfflineManager = () => {
  if (isInitialized) {
    console.warn('[offlineManager] Already initialized');
    return;
  }

  // Set up event listeners
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Process any existing queued requests if we're online
  if (isOnline()) {
    // Delay to ensure app is fully loaded
    setTimeout(() => {
      processQueue();
    }, 2000);
  }

  isInitialized = true;
  console.log('[offlineManager] Initialized');
};

/**
 * Cleanup offline manager
 */
export const cleanupOfflineManager = () => {
  if (!isInitialized) {
    return;
  }

  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);

  isInitialized = false;
  console.log('[offlineManager] Cleaned up');
};

/**
 * Manually trigger queue processing
 */
export const triggerQueueProcessing = () => {
  processQueue();
};

export default {
  init: initOfflineManager,
  cleanup: cleanupOfflineManager,
  processQueue: triggerQueueProcessing,
};
